import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Printer, FilePlus2, FileDown, Eye, MoreHorizontal,
  Link2, ShieldOff, ShieldCheck, QrCode, ExternalLink, Trash2, ClipboardList,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { api, pdfUrl, excelUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function Records() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/certificates", { params: q ? { q } : {} });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const filtered = rows;

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/certificates/${toDelete.id}`);
      toast.success("Record deleted");
      setToDelete(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const openPdf = (r) => window.open(pdfUrl(r.id), "_blank");
  const printPdf = (r) => {
    const w = window.open(pdfUrl(r.id), "_blank");
    if (w) w.addEventListener("load", () => w.print());
  };

  const copyLink = (r) => {
    navigator.clipboard
      .writeText(`${window.location.origin}/verify/${r.verification_id}`)
      .then(() => toast.success("Verification link copied"));
  };

  const regenQr = async (r) => {
    try {
      await api.post(`/certificates/${r.id}/regenerate-qr`);
      toast.success("QR code regenerated");
      load();
    } catch {
      toast.error("Failed to regenerate QR");
    }
  };

  const toggleStatus = async (r) => {
    const ns = r.status === "Active" ? "Revoked" : "Active";
    try {
      await api.post(`/certificates/${r.id}/status`, { status: ns });
      toast.success(ns === "Revoked" ? "Certificate revoked" : "Certificate restored");
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <header className="flex flex-wrap gap-4 items-end justify-between mb-6">
        <div>
          <p className="text-sm font-medium text-blue-800 uppercase tracking-wider">
            Records
          </p>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">
            Student Records
          </h1>
          <p className="text-slate-500 mt-1">
            {filtered.length} certificate{filtered.length === 1 ? "" : "s"} on file.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="export-excel-btn"
            onClick={() => window.open(excelUrl(), "_blank")}
            className="h-10"
          >
            <FileDown size={16} className="mr-2" /> Export Excel
          </Button>
          <Button
            data-testid="new-cert-btn"
            onClick={() => nav("/admin/new")}
            className="h-10 bg-blue-800 hover:bg-blue-900"
          >
            <FilePlus2 size={16} className="mr-2" /> New Certificate
          </Button>
        </div>
      </header>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-md">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, cert no, reg no, enrollment, admission or mobile…"
              className="pl-9 h-10"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Cert No.</th>
                <th className="px-4 py-3 font-medium">Student</th>
                <th className="px-4 py-3 font-medium">Course</th>
                <th className="px-4 py-3 font-medium">Admission</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading…</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                    No records yet — create your first certificate.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`row-${r.id}`}
                  className="border-t border-slate-100 hover:bg-slate-50 transition-soft"
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-blue-800">{r.certificate_number}</span>
                    <span
                      data-testid={`fmt-${r.id}`}
                      className={`ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                        r.format === 2
                          ? "bg-amber-100 text-amber-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {r.format === 2 ? "Marksheet" : "Certificate"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.student_name}</div>
                    {r.father_name && (
                      <div className="text-xs text-slate-500">S/o D/o {r.father_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {r.course_name}
                    <div className="text-xs text-slate-500">{r.course_duration}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.admission_number || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{r.issue_date}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end items-center">
                      {r.status && r.status !== "Active" && (
                        <span
                          data-testid={`status-${r.id}`}
                          className="mr-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                        >
                          {r.status}
                        </span>
                      )}
                      <IconBtn testId={`view-${r.id}`} title="View / Download" onClick={() => openPdf(r)}>
                        <Eye size={16} />
                      </IconBtn>
                      <IconBtn testId={`print-${r.id}`} title="Print" onClick={() => printPdf(r)}>
                        <Printer size={16} />
                      </IconBtn>
                      <IconBtn testId={`edit-${r.id}`} title="Generate again / edit"
                        onClick={() => nav(`/admin/new/${r.id}`)}>
                        <FilePlus2 size={16} />
                      </IconBtn>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`more-${r.id}`}
                            className="p-2 rounded-md border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-soft">
                            <MoreHorizontal size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {Number(r.format) !== 2 && (
                            <DropdownMenuItem data-testid={`create-ms-${r.id}`}
                              onClick={() => nav(`/admin/new?format=2&student=${r.id}`)}>
                              <ClipboardList size={14} className="mr-2" /> Create marksheet
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`view-verify-${r.id}`}
                            onClick={() => window.open(`/verify/${r.verification_id}`, "_blank")}>
                            <ExternalLink size={14} className="mr-2" /> View verification page
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`copy-link-${r.id}`} onClick={() => copyLink(r)}>
                            <Link2 size={14} className="mr-2" /> Copy verification link
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`regen-qr-${r.id}`} onClick={() => regenQr(r)}>
                            <QrCode size={14} className="mr-2" /> Regenerate QR code
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`revoke-${r.id}`} onClick={() => toggleStatus(r)}>
                            {r.status === "Active" ? (
                              <><ShieldOff size={14} className="mr-2" /> Revoke certificate</>
                            ) : (
                              <><ShieldCheck size={14} className="mr-2" /> Restore certificate</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`delete-${r.id}`} className="text-red-600"
                            onClick={() => setToDelete(r)}>
                            <Trash2 size={14} className="mr-2" /> Delete record
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete certificate record?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <b>{toDelete?.certificate_number}</b> for{" "}
              <b>{toDelete?.student_name}</b>. The certificate number is not reused.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              className="bg-red-600 hover:bg-red-700"
              onClick={doDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function IconBtn({ children, danger, testId, ...p }) {
  return (
    <button
      {...p}
      data-testid={testId}
      className={`p-2 rounded-md border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-soft ${
        danger ? "hover:text-red-700 hover:border-red-200" : ""
      }`}
    >
      {children}
    </button>
  );
}
